"use client";

import type { MigrateState, MigrateStore } from "@/features/migrate/types/state";
import { createContext, useRef, useContext, type PropsWithChildren } from "react";
import { useStore } from "zustand";
import { persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";

const defaultInitState: MigrateState = {
  vaultShares: [],
  completeApprovals: [],
  txData: undefined,
  isFinished: false,
};

const createMigrateStore = (initState: MigrateState = defaultInitState) => {
  return createStore<MigrateStore>()(
    persist(
      set => ({
        ...initState,
        setVaultShares: vaultShares => set(() => ({ ...defaultInitState, vaultShares })),
        addCompleteApproval: approval => set(state => ({ ...state, completeApprovals: [...state.completeApprovals, approval] })),
        setTxData: txData => set(state => ({ ...state, txData })),
        setFinished: () => set(state => ({ ...state, isFinished: true })),
        reset: () => set(() => defaultInitState),
      }),
      { name: "zstnd-migrate" }
    )
  );
};

type MigrateStoreApi = ReturnType<typeof createMigrateStore>;

export const MigrateStoreContext = createContext<MigrateStoreApi | undefined>(undefined);

export const MigrateStoreProvider = ({ children }: PropsWithChildren) => {
  const storeRef = useRef<MigrateStoreApi>(undefined);
  if (!storeRef.current) {
    storeRef.current = createMigrateStore();
  }

  return <MigrateStoreContext.Provider value={storeRef.current}>{children}</MigrateStoreContext.Provider>;
};

export const useMigrateStore = <T,>(selector: (store: MigrateStore) => T): T => {
  const migrateStoreContextInstance = useContext(MigrateStoreContext);

  if (!migrateStoreContextInstance) {
    throw new Error(`useMigrateStore must be used within MigrateStoreProvider`);
  }

  return useStore(migrateStoreContextInstance, selector);
};
